/**
 * Something Property — API Contract (Single Source of Truth)
 *
 * Generated from: OpenAPI Spec v1.0.0 + Postman UAT Collection (122/0 pass)
 * API Gateway: ekwjoqrj0g | Stage: dev | Deployment: dk78o8
 * Date: 2026-04-05
 *
 * RULES:
 * - Every API call in the frontend MUST use these types and paths
 * - If a path or type is wrong here, fix it HERE — not in the service file
 * - Backend is FROZEN — these types match what is deployed and passing UAT
 */

// ============================================================
// GENERIC RESPONSE ENVELOPE
// ============================================================

/** Every successful backend response wraps data in this envelope */
export interface ApiResponse<T> {
  success: true;
  data: T;
}

/** Every error response follows this shape */
export interface ApiError {
  success: false;
  error: string;
  message: string;
  statusCode: number;
}

// ============================================================
// ENTITY TYPES (what the backend stores and returns)
// ============================================================

export interface User {
  userId: string;
  email: string;
  name: string;
  groups: string[];
  emailVerified: boolean;
}

export interface Profile {
  userId: string;
  email: string;
  name: string;
  phone?: string;
  role?: string;
  avatarUrl?: string;
  createdAt: number;
  updatedAt: number;
}

export interface Client {
  clientId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  type?: 'buyer' | 'seller' | 'both';
  status?: 'active' | 'inactive';
  notes?: string;
  source?: string;
  createdAt: number;
  updatedAt: number;
}

export interface Appointment {
  appointmentId: string;
  type: 'viewing' | 'meeting' | 'signing' | 'other';
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  scheduledDate: string;
  duration?: number;
  agentId?: string;
  agentName?: string;
  clientName?: string;
  clientEmail?: string;
  listingId?: string;
  propertyAddress?: string;
  createdAt: number;
  updatedAt: number;
}

export interface Seller {
  sellerId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  idNumber?: string;
  status?: 'active' | 'inactive';
  source?: string;
  notes?: string;
  createdAt: number;
  updatedAt: number;
}

export interface SellerProperty {
  id: string;
  sellerId: string;
  propertyAddress?: string;
  status: 'pending' | 'approved' | 'rejected';
  askingPrice?: number;
  bedrooms?: number;
  bathrooms?: number;
  createdAt: number;
  updatedAt: number;
}

export interface Deal {
  dealId: string;
  status: string;
  clientId?: string;
  listingId?: string;
  agentId?: string;
  value?: number;
  createdAt: number;
  updatedAt: number;
}

export interface Milestone {
  id: string;
  dealId: string;
  title: string;
  status: string;
  dueDate?: string;
  completedAt?: number;
  createdAt: number;
  updatedAt: number;
}

export interface Signing {
  id: string;
  dealId?: string;
  status: string;
  scheduledDate?: string;
  location?: string;
  createdAt: number;
  updatedAt: number;
}

export interface Listing {
  id: string;
  title?: string;
  status?: string;
  price?: number;
  address?: string;
  bedrooms?: number;
  bathrooms?: number;
  source?: string;
  createdAt: number;
  updatedAt: number;
}

export interface ImageRecord {
  id: string;
  path?: string;
  url?: string;
  contentType?: string;
  fileName?: string;
  folder?: string;
  createdAt: number;
}

export interface StockPost {
  postId: string;
  title: string;
  content: string;
  author?: string;
  authorId?: string;
  listingId?: string;
  status: 'draft' | 'published' | 'archived';
  createdAt: number;
  updatedAt: number;
}

export interface KnowledgeHubDocument {
  documentId: string;
  title: string;
  category: string;
  subcategory?: string | null;
  description?: string | null;
  fileType: 'pdf' | 'video';
  s3Key: string;
  fileSizeMb?: number | null;
  thumbnailS3Key?: string | null;
  published: boolean;
  uploadedBy: string;
  uploadedAt: string;
  updatedAt: string;
  sortOrder: number;
}

export interface Alert {
  id: string;
  type?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message?: string;
  resolved?: boolean;
  createdAt: number;
}

export interface AuditLog {
  id: string;
  action: string;
  userId?: string;
  resource?: string;
  timestamp: number;
  details?: Record<string, unknown>;
}

export interface Setting {
  key: string;
  value: Record<string, unknown>;
  updatedAt: number;
}

export interface Message {
  messageId: string;
  conversationId: string;
  senderId?: string;
  content: string;
  channel?: 'whatsapp' | 'email' | 'internal';
  status?: string;
  createdAt: number;
}

export interface HealthCheck {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: number;
  version?: string;
  components: Record<string, unknown>;
}

// ============================================================
// REQUEST TYPES (what the frontend sends to the backend)
// ============================================================

// -- Auth --
export interface LoginRequest {
  email: string;    // required
  password: string; // required
}

export interface RegisterRequest {
  email: string;    // required
  password: string; // required
  name: string;     // required
}

export interface RegisterSellerRequest {
  email: string;       // required
  password: string;    // required
  name: string;        // required
  phoneNumber: string; // required — NOTE: not "phone", it's "phoneNumber"
}

export interface ConfirmRequest {
  username: string; // required — this is the email
  code: string;     // required — 6-digit code
}

export interface ResendCodeRequest {
  username: string; // required — this is the email
}

export interface ForgotPasswordRequest {
  email: string; // required
}

export interface ConfirmForgotPasswordRequest {
  email: string;       // required
  code: string;        // required
  newPassword: string; // required
}

export interface ChangePasswordRequest {
  oldPassword: string; // required
  newPassword: string; // required
}

export interface RefreshTokenRequest {
  refreshToken: string; // required
}

export interface LogoutRequest {
  accessToken: string; // required — NOTE: this is the ONLY place accessToken goes in a body
}

// -- Clients --
export interface CreateClientRequest {
  firstName: string; // required — NOT "name"
  lastName: string;  // required — NOT "name"
  email?: string;
  phone?: string;
  status?: string;
  type?: string;
  notes?: string;
}

// -- Sellers --
export interface CreateSellerRequest {
  firstName: string; // required
  lastName: string;  // required
  email?: string;
  phone?: string;
  idNumber?: string;
  notes?: string;
}

// -- Images --
export interface ImageUploadRequest {
  imageBase64: string; // required — NOT "imageData"
  contentType: string; // required — e.g. "image/png"
  fileName: string;    // required
  folder?: string;     // e.g. "test", "properties", "avatars"
}

// -- Profile --
export interface UpdateProfileRequest {
  name?: string;
}

export interface ProfilePhotoRequest {
  imageBase64: string; // required — NOT "imageData"
  contentType: string; // required — NOT "fileName"
}

// -- OTP --
export interface OtpSendRequest {
  email: string; // required — NOT "phone", NOT "method"
}

export interface OtpVerifyRequest {
  email: string; // required — NOT "phone"
  code: string;  // required
}

// -- Listings --
export interface UpdateListingRequest {
  title?: string;
  status?: string;
}

export interface UpdateListingStatusRequest {
  status: string; // required — e.g. "Active"
}

// -- Stock Posts --
export interface CreateStockPostRequest {
  title: string;   // required
  content: string; // required
  listingId?: string;
  author?: string;
  authorId?: string;
}

export interface UpdateStockPostRequest {
  status?: 'draft' | 'published' | 'archived';
  title?: string;
  content?: string;
}

export interface StockPostMessageRequest {
  content: string; // required — NOTE: just "content", NOT userId/userName/text
}

// -- Seller Properties --
export interface RejectSellerPropertyRequest {
  reason: string; // required
}

// -- Admin --
export interface UpdateUserStatusRequest {
  status: 'enabled' | 'disabled'; // required
}

export interface UpdateUserPasswordRequest {
  password: string; // required
}

export interface UpdateSettingRequest {
  value: Record<string, unknown>; // required — NOT { settings, updatedBy }
}

export interface UpdateAlertRequest {
  resolved: boolean;
}

// -- Messaging (External) --
export interface SendEmailRequest {
  to: string;      // required
  subject: string; // required
  body: string;    // required
}

export interface SendWhatsAppRequest {
  to: string;       // required — phone number
  message: string;  // required
}

// ============================================================
// RESPONSE DATA TYPES (what's inside the { success, data } envelope)
// ============================================================

export interface LoginResponseData {
  accessToken: string;
  idToken: string;       // USE THIS as Bearer token
  refreshToken: string;
  expiresIn: number;
  user: User;
}

export interface RefreshTokenResponseData {
  accessToken: string;
  idToken: string;
  expiresIn: number;
  // NOTE: refreshToken is NOT returned — keep the existing one
}

export interface ClientListData {
  clients: Client[];
  total: number;
}

export interface AppointmentListData {
  appointments: Appointment[];
  total: number;
}

export interface SellerListData {
  sellers: Seller[];
  total: number;
}

export interface OtpSendData {
  item: {
    id: string;
    code?: string; // returned in dev only
    expiresIn: number;
  };
}

// ============================================================
// ENDPOINT DEFINITIONS
// ============================================================

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

interface Endpoint<TReq = void, TRes = unknown> {
  method: Method;
  path: string;
  auth: boolean;
  /** TypeScript will enforce request body shape */
  _req?: TReq;
  /** TypeScript will enforce response.data shape */
  _res?: TRes;
}

function endpoint<TReq = void, TRes = unknown>(
  method: Method,
  path: string,
  auth: boolean
): Endpoint<TReq, TRes> {
  return { method, path, auth };
}

// -- Public = no auth required --
const PUBLIC = false;
const AUTH = true;

export const API = {

  // ── Auth ────────────────────────────────────────────────────
  auth: {
    login:              endpoint<LoginRequest, LoginResponseData>('POST', '/auth/login', PUBLIC),
    register:           endpoint<RegisterRequest>('POST', '/auth/register', PUBLIC),
    registerSeller:     endpoint<RegisterSellerRequest>('POST', '/auth/register-seller', PUBLIC),
    confirm:            endpoint<ConfirmRequest>('POST', '/auth/confirm', PUBLIC),
    resendCode:         endpoint<ResendCodeRequest>('POST', '/auth/resend-code', PUBLIC),
    refresh:            endpoint<RefreshTokenRequest, RefreshTokenResponseData>('POST', '/auth/refresh', PUBLIC),
    forgotPassword:     endpoint<ForgotPasswordRequest>('POST', '/auth/forgot-password', PUBLIC),
    confirmForgotPwd:   endpoint<ConfirmForgotPasswordRequest>('POST', '/auth/confirm-forgot-password', PUBLIC),
    changePassword:     endpoint<ChangePasswordRequest>('POST', '/auth/change-password', AUTH),
    logout:             endpoint<LogoutRequest>('POST', '/auth/logout', AUTH),
    getUser:            endpoint<void, User>('GET', '/auth/user', AUTH),
    getGroups:          endpoint('GET', '/auth/groups', AUTH),
    listUsers:          endpoint('GET', '/auth/users', AUTH),
    getUserById:        endpoint('GET', '/auth/users/:userId', AUTH),
  },

  // ── Admin — Users ──────────────────────────────────────────
  admin: {
    getUsers:           endpoint('GET', '/admin/users', AUTH),
    createUser:         endpoint('POST', '/admin/users', AUTH),
    getUserById:        endpoint('GET', '/admin/users/:userId', AUTH),
    updateUser:         endpoint('PUT', '/admin/users/:userId', AUTH),
    deleteUser:         endpoint('DELETE', '/admin/users/:userId', AUTH),
    updateUserStatus:   endpoint<UpdateUserStatusRequest>('PUT', '/admin/users/:userId/status', AUTH),
    updateUserPassword: endpoint<UpdateUserPasswordRequest>('PUT', '/admin/users/:userId/password', AUTH),
    getSettings:        endpoint('GET', '/admin/settings', AUTH),
    getSetting:         endpoint('GET', '/admin/settings/:key', AUTH),
    updateSetting:      endpoint<UpdateSettingRequest>('PUT', '/admin/settings/:key', AUTH),
    getAlerts:          endpoint('GET', '/admin/alerts', AUTH),
    getAlert:           endpoint('GET', '/admin/alerts/:id', AUTH),
    updateAlert:        endpoint<UpdateAlertRequest>('PUT', '/admin/alerts/:id', AUTH),
    getAuditLogs:       endpoint('GET', '/admin/audit-logs', AUTH),
    getAuditLog:        endpoint('GET', '/admin/audit-logs/:id', AUTH),
    // Analytics
    getMetrics:         endpoint('GET', '/admin/analytics/metrics', AUTH),
    getUserGrowth:      endpoint('GET', '/admin/analytics/user-growth', AUTH),
    getRevenue:         endpoint('GET', '/admin/analytics/revenue', AUTH),
    getPropertyListings:endpoint('GET', '/admin/analytics/property-listings', AUTH),
    getPropertyTypes:   endpoint('GET', '/admin/analytics/property-types', AUTH),
    getAgentPerformance:endpoint('GET', '/admin/analytics/agent-performance', AUTH),
    getEngagement:      endpoint('GET', '/admin/analytics/engagement', AUTH),
  },

  // ── Agents ─────────────────────────────────────────────────
  agents: {
    list:               endpoint('GET', '/agents', AUTH),
    directory:          endpoint('GET', '/agent-directory', PUBLIC), // P11 — public, no auth
    getById:            endpoint('GET', '/agents/:id', AUTH),
    sync:               endpoint('POST', '/agents/sync', AUTH),
    createCognitoUsers: endpoint('POST', '/agents/create-cognito-users', AUTH),
    disable:            endpoint('PUT', '/agents/:id/disable', AUTH),
    resetPassword:      endpoint('PUT', '/agents/:id/reset-password', AUTH),
  },

  // ── Appointments ───────────────────────────────────────────
  appointments: {
    list:               endpoint('GET', '/appointments', AUTH),
    getById:            endpoint('GET', '/appointments/:id', AUTH),
    create:             endpoint('POST', '/appointments', AUTH),
    update:             endpoint('PUT', '/appointments/:id', AUTH),
    cancel:             endpoint('PUT', '/appointments/:id/cancel', AUTH),
    delete:             endpoint('DELETE', '/appointments/:id', AUTH),
  },

  // ── Clients ────────────────────────────────────────────────
  clients: {
    list:               endpoint<void, ClientListData>('GET', '/clients', AUTH),
    create:             endpoint<CreateClientRequest, Client>('POST', '/clients', AUTH),
    getById:            endpoint<void, Client>('GET', '/clients/:id', AUTH),
    update:             endpoint('PUT', '/clients/:id', AUTH),
    delete:             endpoint('DELETE', '/clients/:id', AUTH),
  },

  // ── Deals ──────────────────────────────────────────────────
  deals: {
    list:               endpoint('GET', '/deals', AUTH),
    create:             endpoint('POST', '/deals', AUTH),
    getById:            endpoint('GET', '/deals/:id', AUTH),
    update:             endpoint('PUT', '/deals/:id', AUTH),
    delete:             endpoint('DELETE', '/deals/:id', AUTH),
    getMilestones:      endpoint('GET', '/deals/:id/milestones', AUTH),
  },

  // ── Images ─────────────────────────────────────────────────
  images: {
    upload:             endpoint<ImageUploadRequest, ImageRecord>('POST', '/images/upload', AUTH),
    list:               endpoint('GET', '/images', AUTH),
    getById:            endpoint('GET', '/images/:id', AUTH),
    delete:             endpoint('DELETE', '/images/:id', AUTH),
  },

  // ── Listings ───────────────────────────────────────────────
  listings: {
    list:               endpoint('GET', '/listings', AUTH),
    create:             endpoint('POST', '/listings', AUTH),
    getById:            endpoint('GET', '/listings/:id', AUTH),
    update:             endpoint<UpdateListingRequest>('PUT', '/listings/:id', AUTH),
    delete:             endpoint('DELETE', '/listings/:id', AUTH),
    updateStatus:       endpoint<UpdateListingStatusRequest>('PUT', '/listings/:id/status', AUTH),
    sync:               endpoint('POST', '/listings/sync', AUTH),
    getSyncStatus:      endpoint('GET', '/listings/sync/status', AUTH),
  },

  // ── Messages ───────────────────────────────────────────────
  messages: {
    list:               endpoint('GET', '/messages', AUTH),       // ?conversationId=xxx
    getById:            endpoint('GET', '/messages/:messageId', AUTH),
    getHistory:         endpoint('GET', '/messages/history/:conversationId', AUTH),
    getThreads:         endpoint('GET', '/messages/threads', AUTH),
    send:               endpoint('POST', '/messages', AUTH),
  },

  // ── Milestones ─────────────────────────────────────────────
  milestones: {
    list:               endpoint('GET', '/milestones', AUTH),
    getById:            endpoint('GET', '/milestones/:id', AUTH),
    create:             endpoint('POST', '/milestones', AUTH),
    update:             endpoint('PUT', '/milestones/:id', AUTH),
  },

  // ── OTP ────────────────────────────────────────────────────
  otp: {
    send:               endpoint<OtpSendRequest, OtpSendData>('POST', '/otp/send', PUBLIC),
    verify:             endpoint<OtpVerifyRequest>('POST', '/otp/verify', AUTH),
  },

  // ── Seller Properties ──────────────────────────────────────
  sellerProperties: {
    list:               endpoint('GET', '/seller-properties', AUTH),
    create:             endpoint('POST', '/seller-properties', AUTH),
    getById:            endpoint('GET', '/seller-properties/:id', AUTH),
    update:             endpoint('PUT', '/seller-properties/:id', AUTH),
    delete:             endpoint('DELETE', '/seller-properties/:id', AUTH),
    approve:            endpoint('PUT', '/seller-properties/:id/approve', AUTH),
    reject:             endpoint<RejectSellerPropertyRequest>('PUT', '/seller-properties/:id/reject', AUTH),
    getBySeller:        endpoint('GET', '/seller-properties/seller/:sellerId', AUTH),
  },

  // ── Sellers ────────────────────────────────────────────────
  sellers: {
    list:               endpoint<void, SellerListData>('GET', '/sellers', AUTH),
    create:             endpoint<CreateSellerRequest>('POST', '/sellers', AUTH),
    getById:            endpoint('GET', '/sellers/:id', AUTH),
    update:             endpoint('PUT', '/sellers/:id', AUTH),
  },

  // ── Signings ───────────────────────────────────────────────
  signings: {
    list:               endpoint('GET', '/signings', AUTH),
    create:             endpoint('POST', '/signings', AUTH),
    getById:            endpoint('GET', '/signings/:id', AUTH),
    update:             endpoint('PUT', '/signings/:id', AUTH),
    delete:             endpoint('DELETE', '/signings/:id', AUTH),
    getHistory:         endpoint('GET', '/signings/:id/history', AUTH),
  },

  // ── Stock Posts ────────────────────────────────────────────
  stockPosts: {
    list:               endpoint('GET', '/stock-posts', AUTH),
    create:             endpoint<CreateStockPostRequest>('POST', '/stock-posts', AUTH),
    getById:            endpoint('GET', '/stock-posts/:id', AUTH),
    update:             endpoint<UpdateStockPostRequest>('PUT', '/stock-posts/:id', AUTH),
    delete:             endpoint('DELETE', '/stock-posts/:id', AUTH),
    getMessages:        endpoint('GET', '/stock-posts/:id/messages', AUTH),
    addMessage:         endpoint<StockPostMessageRequest>('POST', '/stock-posts/:id/messages', AUTH),
    fixStatus:          endpoint('POST', '/stock-posts/fix-status', AUTH),
  },

  // ── System ─────────────────────────────────────────────────
  system: {
    health:             endpoint<void, HealthCheck>('GET', '/system/health', PUBLIC),
    property24Health:   endpoint('POST', '/system/property24-health', AUTH),
    status:             endpoint('GET', '/system/status', AUTH),
    costReport:         endpoint('GET', '/system/cost-report', AUTH),
    configuration:      endpoint('GET', '/system/configuration', AUTH),
    metrics:            endpoint('GET', '/system/metrics', AUTH),
    createLogs:         endpoint('POST', '/system/logs', PUBLIC), // P7 — public, no auth
  },

  // ── Users / Profile ────────────────────────────────────────
  users: {
    getProfile:         endpoint<void, Profile>('GET', '/users/profile', AUTH),
    updateProfile:      endpoint<UpdateProfileRequest, Profile>('PUT', '/users/profile', AUTH),
    uploadPhoto:        endpoint<ProfilePhotoRequest>('POST', '/users/profile/photo', AUTH),
    ensureProfile:      endpoint<void, Profile>('POST', '/users/profile/ensure', AUTH),
  },

  // ── Rentals ────────────────────────────────────────────────
  rentals: {
    getLeases:          endpoint('GET', '/rentals/leases', AUTH),
    getApplications:    endpoint('GET', '/rentals/applications', AUTH),
  },

  // ── AI Agent ───────────────────────────────────────────────
  aiAgent: {
    getStats:           endpoint('GET', '/ai-agent/stats', AUTH),
  },

  // ── Messaging (External — SES / WhatsApp) ──────────────────
  messaging: {
    sendEmail:          endpoint<SendEmailRequest>('POST', '/messaging/email', AUTH),
    sendEmailTemplate:  endpoint('POST', '/messaging/email/template', AUTH),
    getEmailTemplates:  endpoint('GET', '/messaging/email/templates', AUTH),
    sendWelcomeEmail:   endpoint('POST', '/messaging/email/welcome', AUTH),
    sendPasswordReset:  endpoint('POST', '/messaging/email/password-reset', AUTH),
    sendWhatsApp:       endpoint<SendWhatsAppRequest>('POST', '/messaging/whatsapp', AUTH),
    sendWhatsAppTemplate: endpoint('POST', '/messaging/whatsapp/template', AUTH),
    getWhatsAppTemplates: endpoint('GET', '/messaging/whatsapp/templates', AUTH),
  },

  // ── Knowledge Hub ──────────────────────────────────────────
  knowledgeHub: {
    getDocuments:       endpoint('GET', '/knowledge-hub', AUTH),
    getDocument:        endpoint('GET', '/knowledge-hub/:id', AUTH),
    adminGetDocuments:  endpoint('GET', '/admin/knowledge-hub', AUTH),
    adminUpload:        endpoint('POST', '/admin/knowledge-hub', AUTH),
    adminUpdate:        endpoint('PUT', '/admin/knowledge-hub/:id', AUTH),
    adminDelete:        endpoint('DELETE', '/admin/knowledge-hub/:id', AUTH),
  },

} as const;

// ============================================================
// HELPER: Replace path params — e.g. '/clients/:id' → '/clients/abc123'
// ============================================================

export function buildPath(path: string, params?: Record<string, string>): string {
  if (!params) return path;
  let result = path;
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(`:${key}`, encodeURIComponent(value));
  }
  return result;
}

// Usage example:
// const url = buildPath(API.clients.getById.path, { id: 'abc123' });
// → '/clients/abc123'
